import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  Collapse,
  AppBar,
  Toolbar,
} from '@material-ui/core';
import {
  Notifications as NotificationsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Event as EventIcon,
  Business as BusinessIcon,
  Refresh as RefreshIcon,
} from '@material-ui/icons';
import {
  useUpcomingTasksNotification,
  UpcomingTask,
} from '../hooks/useUpcomingTasksNotification';

interface UpcomingTasksNotificationsProps {
  isRTL: boolean;
}

export const UpcomingTasksNotifications: React.FC<
  UpcomingTasksNotificationsProps
> = ({ isRTL }) => {
  const [expanded, setExpanded] = useState(false);
  const { upcomingTasks, refreshTasks, hasUpcomingTasks } =
    useUpcomingTasksNotification(isRTL);

  if (!hasUpcomingTasks) {
    return null;
  }

  const getTaskMessage = (task: UpcomingTask) => {
    const customerName = isRTL ? task.customerNameAr : task.customerName;
    const taskName = isRTL
      ? task.nameAr || task.title
      : task.name || task.title;

    if (task.daysUntilStart === 0) {
      return isRTL
        ? `تبدأ اليوم - ${taskName} للعميل ${customerName}`
        : `Starts today - ${taskName} for customer ${customerName}`;
    } else if (task.daysUntilStart === 1) {
      return isRTL
        ? `تبدأ غداً - ${taskName} للعميل ${customerName}`
        : `Starts tomorrow - ${taskName} for customer ${customerName}`;
    } else {
      return isRTL
        ? `تبدأ خلال ${task.daysUntilStart} أيام - ${taskName} للعميل ${customerName}`
        : `Starts in ${task.daysUntilStart} days - ${taskName} for customer ${customerName}`;
    }
  };

  const getDaysColor = (days: number) => {
    if (days === 0) return '#f44336'; // Red for today
    if (days === 1) return '#ff9800'; // Orange for tomorrow
    if (days <= 3) return '#ff9800'; // Orange for urgent
    return '#2196f3'; // Blue for future
  };

  const getDaysLabel = (days: number) => {
    if (days === 0) return isRTL ? 'اليوم' : 'Today';
    if (days === 1) return isRTL ? 'غداً' : 'Tomorrow';
    return isRTL ? `${days} أيام` : `${days} days`;
  };

  return (
    <Box
      style={{
        position: 'fixed',
        top: 80,
        right: isRTL ? 'auto' : 20,
        left: isRTL ? 20 : 'auto',
        zIndex: 1300,
        maxWidth: 400,
      }}
    >
      <Card style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
        <AppBar position="static" style={{ backgroundColor: '#2196f3' }}>
          <Toolbar style={{ padding: '0 16px', minHeight: '48px' }}>
            <Box display="flex" alignItems="center" flexGrow={1}>
              <NotificationsIcon style={{ marginRight: 8 }} />
              <Typography variant="h6" style={{ fontSize: '16px' }}>
                {isRTL ? 'مهام قادمة' : 'Upcoming Tasks'}
              </Typography>
              <Chip
                label={upcomingTasks.length}
                size="small"
                style={{
                  marginLeft: 8,
                  backgroundColor: '#fff',
                  color: '#2196f3',
                  fontWeight: 'bold',
                }}
              />
            </Box>
            <Box display="flex" alignItems="center">
              <IconButton
                size="small"
                onClick={refreshTasks}
                style={{ color: '#fff', marginRight: 4 }}
              >
                <RefreshIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setExpanded(!expanded)}
                style={{ color: '#fff' }}
              >
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <CardContent
            style={{ padding: 0, maxHeight: 400, overflowY: 'auto' }}
          >
            <List dense>
              {upcomingTasks.map((task) => (
                <ListItem
                  key={task._id}
                  style={{ borderBottom: '1px solid #eee' }}
                >
                  <ListItemText
                    primary={
                      <Box>
                        <Typography
                          variant="subtitle2"
                          style={{ fontWeight: 'bold' }}
                        >
                          {isRTL ? `مهمة ${task.docNo}` : `Task ${task.docNo}`}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {getTaskMessage(task)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box display="flex" alignItems="center" marginTop={4}>
                        <EventIcon
                          style={{
                            fontSize: 14,
                            marginRight: 4,
                            color: '#666',
                          }}
                        />
                        <Typography
                          variant="caption"
                          style={{ marginRight: 8 }}
                        >
                          {new Date(task.start).toLocaleDateString()}
                        </Typography>
                        <BusinessIcon
                          style={{
                            fontSize: 14,
                            marginRight: 4,
                            color: '#666',
                          }}
                        />
                        <Typography variant="caption">
                          {isRTL ? task.customerNameAr : task.customerName}
                        </Typography>
                      </Box>
                    }
                  />
                  <Chip
                    label={getDaysLabel(task.daysUntilStart)}
                    size="small"
                    style={{
                      backgroundColor: getDaysColor(task.daysUntilStart),
                      color: '#fff',
                      fontWeight: 'bold',
                      minWidth: 60,
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Collapse>
      </Card>
    </Box>
  );
};
